export class DateHelper {
  /**
   * Gets a local date adjusted for timezone to prevent UTC conversion issues when sending to API
   * @param date Optional date to use, defaults to current date
   * @returns Date object that represents local midnight adjusted for timezone offset
   */
  public static getLocalDate(date?: Date): Date {
    const targetDate = date || new Date();

    // Set to local midnight
    const localDate = new Date(targetDate);
    // localDate.setHours(0, 0, 0, 0);

    // Get timezone offset in milliseconds and adjust to prevent UTC conversion issues
    const offsetMs = localDate.getTimezoneOffset() * 60000;
    const adjustedDate = new Date(localDate.getTime() - offsetMs);

    return adjustedDate;
  }

  /**
   * Gets the current local date as a timezone-safe date for API consumption
   * @returns Date object representing today in user's timezone
   */
  public static getTodayLocal(): Date {
    return this.getLocalDate();
  }

  /**
   * Formats the current local date to YYYY-MM-DD HH:mm:ss format for API consumption
   * @returns Formatted local date string in YYYY-MM-DD HH:mm:ss format
   */
  public static formatToApiDateTime(): string {
    const localDate = this.getLocalDate();
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, "0");
    const day = String(localDate.getDate()).padStart(2, "0");
    const hours = String(localDate.getHours()).padStart(2, "0");
    const minutes = String(localDate.getMinutes()).padStart(2, "0");
    const seconds = String(localDate.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  /**
   * The most recent 4:59am local boundary — content dated before it belongs to the
   * previous day. Matches ScoreLocalService.getCurrentDayBoundary()'s reset timing.
   * @param now Optional reference time, defaults to now
   * @returns Date object for the most recent 4:59am
   */
  public static getCurrentDayBoundary(now: Date = new Date()): Date {
    const boundary = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 4, 59, 0, 0);
    if (now < boundary) {
      boundary.setDate(boundary.getDate() - 1);
    }
    return boundary;
  }

  /**
   * The next upcoming 4:59am local boundary, for scheduling a precise one-shot timer
   * rather than polling.
   * @param now Optional reference time, defaults to now
   * @returns Date object for the next 4:59am
   */
  public static getNextDayBoundary(now: Date = new Date()): Date {
    const boundary = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 4, 59, 0, 0);
    if (now >= boundary) {
      boundary.setDate(boundary.getDate() + 1);
    }
    return boundary;
  }

  /**
   * Midnight of the "app day" - the calendar date the 4:59am boundary belongs to. Unlike
   * getCurrentDayBoundary(), this is safe to compare against calendar-day fields that are
   * always stored at midnight (e.g. PatientPlanDto.startDate): those fields never fall
   * after a same-day 4:59am boundary, so a raw >= boundary comparison against them is
   * always false. Match a calendar-day field against this app day's midnight-to-midnight
   * range instead.
   * @param now Optional reference time, defaults to now
   * @returns Date object at midnight of the app day
   */
  public static getCurrentAppDay(now: Date = new Date()): Date {
    const boundary = this.getCurrentDayBoundary(now);
    return new Date(boundary.getFullYear(), boundary.getMonth(), boundary.getDate());
  }

  /**
   * Gets a UTC DateTime from a local date
   * @param date The date to convert
   * @returns Date object in UTC
   */
  public static getUTCDateTime(date: Date): Date {
    const result = new Date();
    result.setUTCFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    result.setUTCHours(
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds()
    );
    return result;
  }
}
